import { getOpponentFromPlayerId, isEventStarted } from 'app/utils/match';
import type { AutocompleteOption } from 'app/utils/fuzzysearch';
import { marketGroupsDef } from 'app/utils/markets/market-groups';
import marketOrder from 'app/utils/markets/market-order.json';
import { hasEventSuffix } from 'app/utils/markets';
import {
  PLAYER_MARKET_CATEGORY,
  PLAYER_MOST_KILLS_MATCHUP_CATEGORY,
  PLAYER_FIXED_LINES_CATEGORY,
  PREBUILT_CATEGORY,
  PREBUILT_STORYBUILDER_TEMPLATE,
  PLAYER_HEADSHOTS_CATEGORY,
  PLAYER_HEADSHOTS_TEMPLATE_PART,
  PLAYER_ASSISTS_CATEGORY,
  PLAYER_ASSISTS_TEMPLATE_PART,
  PLAYER_MARKET_KILLS_CATEGORY_PART
} from 'app/constants/markets';
import type {
  PandapiMatchWithCompetitorsWithStatus,
  CompetitorOddsWithStatus
} from 'app/pages/EventsPage/types';
import { type MatchPageView } from 'app/constants/routes';
import { isMatchPageView } from 'app/constants/helpers';
import { isStarPlayerMarketTemplate } from 'app/config/markets.config.helper';
import { sortBy, updateOrInsertInArray } from 'app/utils/array';
import { TRADER_INPUTS_CATEGORY_MAPPING } from 'app/config/trader_inputs.config';
import { COMPETITORS_NAME } from 'app/config/competitors.config';

import type {
  HashiData,
  MarketGroupData,
  MatchData,
  PlayerMostKillMatchup,
  UpdatesForBetbuilder
} from './types';

function isMatchTradable(match: PandapiMatch, markets: MatchMarketBlocks): boolean {
  const notStarted = !isEventStarted(match);
  const hasMarkets =
    markets.market_blocks.length > 0 ||
    markets.games.some(({ market_blocks: markets }) => !!markets && markets.length > 0);

  return notStarted || hasMarkets;
}

function getWinnerMarkets(markets: MarketBlock[]): MarketBlock[] {
  return markets.filter(
    ({ mostBalancedMarket }) =>
      mostBalancedMarket && mostBalancedMarket.template.indexOf('winner') >= 0
  );
}

const getOverridenMarketCategory = (market: MarketBlock): string | null => {
  if (market.mostBalancedMarket.template.includes(PLAYER_HEADSHOTS_TEMPLATE_PART)) {
    return PLAYER_HEADSHOTS_CATEGORY;
  }
  if (market.mostBalancedMarket.template.includes(PLAYER_ASSISTS_TEMPLATE_PART)) {
    return PLAYER_ASSISTS_CATEGORY;
  }
  return market.category;
};

function groupMarkets(
  match: PandapiMatch,
  eventType: BlueprintEventType,
  eventId: number,
  markets: MarketBlock[],
  rosters?: PandapiOpponents
): MarketGroupData[] {
  const otherGroupCategory = 'other';
  const otherGroupLabel = 'Other';
  const groups: MarketGroupData[] = [];
  const videogame = match.videogame.slug;

  markets.forEach(marketBlock => {
    const marketCategory = getOverridenMarketCategory(marketBlock);
    const marketGroupDef = marketGroupsDef.find(({ category }) => category === marketCategory);

    // Market in marketGroupDef
    if (marketGroupDef) {
      const {
        category,
        label,
        mainSlugs,
        eventsWithSupremacy,
        eventsWithTotals,
        videogamesWithSupremacy,
        videogamesWithTotals
      } = marketGroupDef;

      if (category === PLAYER_MOST_KILLS_MATCHUP_CATEGORY) {
        const mostKillsMatchUpGroup = groups.find(
          g => g.category === PLAYER_MOST_KILLS_MATCHUP_CATEGORY
        );

        if (mostKillsMatchUpGroup) {
          mostKillsMatchUpGroup.mainMarkets.push(marketBlock);
        } else {
          // Specific to players kills matchups since it is not team related
          const groupLabel = `Players - Matchups`;
          const groupCategory = PLAYER_MOST_KILLS_MATCHUP_CATEGORY;

          const newGroup: MarketGroupData = {
            category: groupCategory,
            videogameSlug: videogame,
            eventType,
            eventId,
            label: groupLabel,
            mainMarkets: [marketBlock],
            markets: [],
            withSupremacy: false,
            withTotals: false,
            participantId: null
          };
          groups.push(newGroup);
        }
      } else if (category === PLAYER_HEADSHOTS_CATEGORY && rosters?.opponent_type === 'Team') {
        // Player markets
        const opponentTeam = getOpponentFromPlayerId(
          marketBlock.mostBalancedMarket.participant_id,
          rosters.opponents
        );

        const teamGroup = groups.find(
          g => g.category === `${opponentTeam?.slug}-${PLAYER_HEADSHOTS_CATEGORY}`
        );

        // if team group already exist we add
        if (teamGroup) {
          teamGroup.mainMarkets.push(marketBlock);
        } else {
          const teamGroupCategory = `${opponentTeam?.slug}-${PLAYER_HEADSHOTS_CATEGORY}`;
          const teamGroupLabel = `${opponentTeam?.name} - Players - Headshots`;

          const newGroup: MarketGroupData = {
            category: teamGroupCategory,
            videogameSlug: videogame,
            eventType,
            eventId,
            label: teamGroupLabel,
            mainMarkets: [marketBlock],
            markets: [],
            withSupremacy: false,
            withTotals: false,
            participantId: opponentTeam?.id ?? null
          };
          groups.push(newGroup);
        }
      } else if (category === PLAYER_ASSISTS_CATEGORY && rosters?.opponent_type === 'Team') {
        // Player markets
        const opponentTeam = getOpponentFromPlayerId(
          marketBlock.mostBalancedMarket.participant_id,
          rosters.opponents
        );

        const teamGroup = groups.find(
          g => g.category === `${opponentTeam?.slug}-${PLAYER_ASSISTS_CATEGORY}`
        );

        // if team group already exist we add
        if (teamGroup) {
          teamGroup.mainMarkets.push(marketBlock);
        } else {
          const teamGroupCategory = `${opponentTeam?.slug}-${PLAYER_ASSISTS_CATEGORY}`;
          const teamGroupLabel = `${opponentTeam?.name} - Players - Assists`;

          const newGroup: MarketGroupData = {
            category: teamGroupCategory,
            videogameSlug: videogame,
            eventType,
            eventId,
            label: teamGroupLabel,
            mainMarkets: [marketBlock],
            markets: [],
            withSupremacy: false,
            withTotals: false,
            participantId: opponentTeam?.id ?? null
          };
          groups.push(newGroup);
        }
      } else if (category === PLAYER_MARKET_CATEGORY && rosters?.opponent_type === 'Team') {
        // Player markets
        const opponentTeam = getOpponentFromPlayerId(
          marketBlock.mostBalancedMarket.participant_id,
          rosters.opponents
        );

        const teamGroup = groups.find(g => g.category === `${opponentTeam?.slug}-players`);

        // if team group already exist we add
        if (teamGroup) {
          teamGroup.mainMarkets.push(marketBlock);
        } else {
          const teamGroupCategory = `${opponentTeam?.slug}-players`;
          const teamGroupLabel = `${opponentTeam?.name} - Players`;

          const newGroup: MarketGroupData = {
            category: teamGroupCategory,
            videogameSlug: videogame,
            eventType,
            eventId,
            label: teamGroupLabel,
            mainMarkets: [marketBlock],
            markets: [],
            withSupremacy: false,
            withTotals: false,
            participantId: opponentTeam?.id ?? null
          };
          groups.push(newGroup);
        }
      } else if (category === PLAYER_FIXED_LINES_CATEGORY && rosters?.opponent_type === 'Team') {
        // When creating fixed lines, both opponents are created, but an opponent can have empty selection
        if (marketBlock.mostBalancedMarket.selections.length > 0) {
          const opponentTeam = rosters.opponents.find(
            opponent => opponent.id === marketBlock.mostBalancedMarket.participant_id
          );

          const teamGroup = groups.find(
            g => g.category === `${opponentTeam?.slug}-players_fixed_lines`
          );

          if (teamGroup) {
            teamGroup.mainMarkets.push(marketBlock);
          } else {
            const teamGroupCategory = `${opponentTeam?.slug}-players_fixed_lines`;
            const teamGroupLabel = `${opponentTeam?.name} - Players - Fixed Lines`;

            const newGroup: MarketGroupData = {
              category: teamGroupCategory,
              videogameSlug: videogame,
              eventType,
              eventId,
              label: teamGroupLabel,
              mainMarkets: [marketBlock],
              markets: [],
              withSupremacy: false,
              withTotals: false,
              participantId: opponentTeam?.id ?? null
            };
            groups.push(newGroup);
          }
        }
        // } else if (category === 'micro') {
      } else {
        const isMain = mainSlugs.some(slug => slug === marketBlock.mostBalancedMarket.template);
        const mainMarkets = isMain ? [marketBlock] : [];
        const restMarkets = isMain ? [] : [marketBlock];

        const group = groups.find(g => g.category === category);

        // We add if group already exist
        if (group) {
          group.mainMarkets = [...group.mainMarkets, ...mainMarkets];
          group.markets = [...group.markets, ...restMarkets];
        } else {
          // We create a new group if he does not exist

          const newGroup: MarketGroupData = {
            category,
            videogameSlug: videogame,
            eventType,
            eventId,
            label,
            mainMarkets,
            markets: restMarkets,
            withSupremacy:
              eventsWithSupremacy.includes(eventType) &&
              videogamesWithSupremacy.includes(videogame),
            withTotals:
              eventsWithTotals.includes(eventType) && videogamesWithTotals.includes(videogame),
            participantId: null
          };
          groups.push(newGroup);
        }
      }
    } else {
      // Other markets
      const otherGroup = groups.find(g => g.category === otherGroupCategory);
      // if other group already exist we add
      if (otherGroup) {
        otherGroup.mainMarkets.push(marketBlock);
      } else {
        // if other group does not exist we create him
        const newGroup: MarketGroupData = {
          category: otherGroupCategory,
          videogameSlug: videogame,
          eventType,
          eventId,
          label: otherGroupLabel,
          mainMarkets: [marketBlock],
          markets: [],
          withSupremacy: false,
          withTotals: false,
          participantId: null
        };
        groups.push(newGroup);
      }
    }
  });

  return groups;
}

/**
 * function to add match winner market in game winner markets
 */
function appendWinnerMarkets(
  marketGroups: MarketGroupData[],
  winnerMarkets: MarketBlock[],
  matchId: number,
  videogameSlug: VideogameSlug
): MarketGroupData[] {
  if (winnerMarkets.length === 0) {
    return marketGroups;
  }
  const matchMarkets: MarketBlock[] = winnerMarkets.map(marketBlock => ({
    ...marketBlock,
    mostBalancedMarket: {
      ...marketBlock.mostBalancedMarket,
      name: `${marketBlock.mostBalancedMarket.name} - MATCH`
    }
  }));

  const winnerGroupIndex = marketGroups.findIndex(
    ({ category: name }) => name.indexOf('winner') >= 0
  );
  if (winnerGroupIndex < 0) {
    const marketWinnerGroup: MarketGroupData = {
      category: 'winner',
      videogameSlug,
      eventType: 'match',
      eventId: matchId,
      label: 'Winner',
      mainMarkets: matchMarkets,
      markets: [],
      withSupremacy: true,
      withTotals: false,
      participantId: null
    };

    return [marketWinnerGroup, ...marketGroups];
  }
  const updatedGroups = [...marketGroups];
  const winnerGroup = updatedGroups[winnerGroupIndex];
  const gameMainMarkets: MarketBlock[] = winnerGroup.mainMarkets.map(marketBlock => {
    const suffix = hasEventSuffix(marketBlock) ? ' - GAME' : '';

    return {
      ...marketBlock,
      market: {
        ...marketBlock.mostBalancedMarket,
        name: `${marketBlock.mostBalancedMarket.name}${suffix}`
      }
    };
  });
  const gameMarkets: MarketBlock[] = winnerGroup.markets.map(marketBlock => {
    const suffix = hasEventSuffix(marketBlock) ? ' - GAME' : '';

    return {
      ...marketBlock,
      market: {
        ...marketBlock.mostBalancedMarket,
        name: `${marketBlock.mostBalancedMarket.name}${suffix}`
      }
    };
  });

  const updatedWinnerGroup: MarketGroupData = {
    ...winnerGroup,
    mainMarkets: [...matchMarkets, ...gameMainMarkets],
    markets: gameMarkets
  };

  updatedGroups[winnerGroupIndex] = updatedWinnerGroup;

  return updatedGroups;
}

const getPlayerTraderInputTypeForCategory = (
  category: string | undefined
): TraderInputItem['type'] | null => {
  if (!category) {
    return null;
  }
  if (category.includes(PLAYER_ASSISTS_TEMPLATE_PART)) {
    return 'assist_totals';
  }
  if (category.includes(PLAYER_MARKET_KILLS_CATEGORY_PART)) {
    return 'kill_totals';
  }
  return null;
};

function getMarketGroupTraderInputs(
  eventType: BlueprintEventType,
  eventId: number,
  groupCategory: string,
  inputs: TraderInputItem[]
): {
  totals: number;
  supremacy: number;
  first_supremacy: number;
  players: TraderInputPlayerStatValue[];
  supremacy_mode: BookmakerInputMode;
  reset_value_when_event_starts: boolean;
  momentumSupremacy: number | null;
} {
  const groupInputs = inputs.filter(
    ({ event_type, event_id, category }) =>
      event_type === eventType &&
      event_id === eventId &&
      (groupCategory === category ||
        TRADER_INPUTS_CATEGORY_MAPPING[groupCategory] === category ||
        groupCategory.includes('-players'))
  );
  const totalsInput = groupInputs.find(({ type }) => type === 'totals');
  const supremacyInput = groupInputs.find(({ type }) => type === 'supremacy');
  const firstSupremacyInput = groupInputs.find(({ type }) => type === 'first_supremacy');
  const playerMarketType = getPlayerTraderInputTypeForCategory(groupCategory);
  const playersInput = groupInputs.find(
    ({ type, category }) => type === playerMarketType && category === PLAYER_MARKET_CATEGORY
  );
  const momentumSupremacyInput = groupInputs
    .filter(({ category }) => category === groupCategory)
    .find(({ type }) => type === 'momentum_adjustment_supremacy');

  const momentumSupremacy = momentumSupremacyInput ? momentumSupremacyInput.value * 100 : null;
  const totals = totalsInput ? totalsInput.value * 100 : 0;
  const supremacy = supremacyInput ? supremacyInput.value * 100 : 0;
  const first_supremacy = firstSupremacyInput ? firstSupremacyInput.value * 100 : 0;
  const players = playersInput
    ? playersInput.value.map((item: TraderInputPlayerStatValue) => ({
        ...item,
        value: item.value
      }))
    : [];

  return {
    totals,
    supremacy,
    first_supremacy,
    players,
    supremacy_mode: supremacyInput?.mode ?? 'basic',
    reset_value_when_event_starts: supremacyInput?.reset_value_when_event_starts ?? false,
    momentumSupremacy
  };
}

function compareStrings(slugA: string, slugB: string): number {
  if (slugA > slugB) return 1;
  if (slugA < slugB) return -1;
  return 0;
}

const getLineAndTemplate = (
  market: MarketBlock | MicroMarket
): { line: string; template: string; ebattlers_index: number | null } => {
  const data = 'mostBalancedMarket' in market ? market.mostBalancedMarket : market;
  return {
    line: data.line || '',
    template: data.template,
    ebattlers_index: data.quarter_index ?? data.half_index ?? null
  };
};

function sortMarkets<T extends MarketBlock | MicroMarket>(markets: T[]): T[] {
  const marketsWithOverriddenCategory = markets.map(market => {
    const { template } = getLineAndTemplate(market);

    if (template === PREBUILT_STORYBUILDER_TEMPLATE) {
      return {
        ...market,
        category: PREBUILT_STORYBUILDER_TEMPLATE
      };
    }
    return market;
  });

  return marketsWithOverriddenCategory.sort((marketA, marketB) => {
    const {
      template: slugA,
      line: lineA,
      ebattlers_index: ebattlers_indexA
    } = getLineAndTemplate(marketA);
    const {
      template: slugB,
      line: lineB,
      ebattlers_index: ebattlers_indexB
    } = getLineAndTemplate(marketB);
    const slugIndexA = marketOrder.indexOf(slugA);
    const slugIndexB = marketOrder.indexOf(slugB);

    if (
      marketA.category === PREBUILT_STORYBUILDER_TEMPLATE &&
      marketB.category === PREBUILT_STORYBUILDER_TEMPLATE
    ) {
      return compareStrings(marketA.id, marketB.id);
    }

    // we avoid to redorder for player markets
    if (marketA.category === 'players' || marketB.category === 'players') {
      return 0;
    }

    // if slug are equals, we sort by ebattlers half/quarter indexes or line
    if (slugA === slugB) {
      // we compare ebattlers half/quarter indexes if they are defined
      if (ebattlers_indexA !== null && ebattlers_indexB !== null) {
        return ebattlers_indexA - ebattlers_indexB;
      }

      const parsedLineA = parseFloat(lineA);
      const parsedLineB = parseFloat(lineB);
      // if lines are numbers, we use number comparison instead of string comparison
      // to avoid "3" > "10"
      if (!isNaN(parsedLineA) && !isNaN(parsedLineB)) {
        return parsedLineA - parsedLineB;
      }
      return compareStrings(lineA, lineB);
    }

    // if both slug aren't declare in order file, we sort by alphabetic order
    if (slugIndexA === -1 && slugIndexB === -1) {
      return compareStrings(slugA, slugB);
    }
    // if one of slug isn't declare in order file, it's push at the end of list
    if (slugIndexA === -1) {
      return 1;
    }
    if (slugIndexB === -1) {
      return -1;
    }

    return slugIndexA - slugIndexB;
  });
}

function getMarketSuggestions(
  markets: MatchMarketBlocks
): AutocompleteOption<{ suffix: string; market: Market }>[] {
  const matchMarketSuggestions = markets.market_blocks
    ? markets.market_blocks.map(({ mostBalancedMarket }) => {
        const marketName = mostBalancedMarket.name.replace(mostBalancedMarket.line ?? '', '');

        return {
          key: mostBalancedMarket.id,
          word: marketName,
          value: {
            market: mostBalancedMarket,
            suffix: 'M'
          }
        };
      })
    : [];

  const gameMarketSuggestions = markets.games.reduce((suggestions, game, index) => {
    if (!game.market_blocks) {
      return suggestions;
    }
    const gameSuggestions = game.market_blocks.map(({ mostBalancedMarket }) => {
      const marketName = mostBalancedMarket.name.replace(mostBalancedMarket.line ?? '', '');

      return {
        key: mostBalancedMarket.id,
        word: marketName,
        value: {
          market: mostBalancedMarket,
          suffix: `G${index + 1}`
        }
      };
    });

    return [...suggestions, ...gameSuggestions];
  }, [] as AutocompleteOption<{ suffix: string; market: Market }>[]);

  return [...matchMarketSuggestions, ...gameMarketSuggestions];
}

function isRosterTeamType(
  toBeDetermined: PandapiOpponents
): toBeDetermined is PandapiTeamOpponents {
  if ((toBeDetermined as PandapiTeamOpponents).opponent_type === 'Team') {
    return true;
  }

  return false;
}

function isPlayerMarketCreated(
  marketGroups: MarketGroupData[],
  marketSlug: string,
  player: PandapiPlayer
): boolean {
  const playerMarkets = marketGroups.find(market => market.category === marketSlug);
  if (!playerMarkets) {
    return false;
  }

  const playerMarket = playerMarkets.mainMarkets.find(item => item.id?.includes(`${player.id}`));

  if (playerMarket) {
    return true;
  }

  return false;
}

const getCreatedPlayersMarkets = (
  opponent: PandapiTeamWithRoster,
  marketSlug: string,
  marketGroups: MarketGroupData[]
): PandapiPlayer[] =>
  opponent.players.filter(player => isPlayerMarketCreated(marketGroups, marketSlug, player));

function isAllPlayerMarketsCreated(
  marketGroups: MarketGroupData[],
  rosters: PandapiOpponents | undefined
): boolean {
  if (!rosters) {
    return true;
  }
  if (!isRosterTeamType(rosters)) {
    return true;
  }

  const opponent1 = rosters.opponents[0];
  const opponent2 = rosters.opponents[1];

  if (opponent1 && opponent2) {
    const opponent1PlayerMarkets = marketGroups.find(
      market => market.category === `${opponent1.slug}-players`
    );
    const opponent2PlayerMarkets = marketGroups.find(
      market => market.category === `${opponent2.slug}-players`
    );

    if (
      opponent1PlayerMarkets?.mainMarkets &&
      opponent1PlayerMarkets?.mainMarkets.length >= 1 &&
      opponent2PlayerMarkets?.mainMarkets &&
      opponent2PlayerMarkets?.mainMarkets.length >= 1
    ) {
      return true;
    }
  }

  return false;
}

function orderMarketsDescending(markets: Market[]): Market[] {
  return markets.sort((a, b) => {
    const lineA = a.line ?? '';
    const lineB = b.line ?? '';
    const parsedLineA = parseFloat(lineA);
    const parsedLineB = parseFloat(lineB);
    if (!isNaN(parsedLineA) && !isNaN(parsedLineB)) {
      return parsedLineB - parsedLineA;
    }
    return compareStrings(lineA, lineB);
  });
}

type PlayerMarketGroupCategory =
  | 'matchups'
  | 'players-home'
  | 'players-away'
  | 'players-fixedLines-home'
  | 'players-fixedLines-away'
  | 'players-headshots-home'
  | 'players-headshots-away'
  | 'players-assists-home'
  | 'players-assists-away';

const getPlayerMarketGroupCategory = (
  category: string,
  participantId: number | null,
  participantHomeId: number | undefined,
  participantAwayId: number | undefined
): PlayerMarketGroupCategory | null => {
  if (category === PLAYER_MOST_KILLS_MATCHUP_CATEGORY) {
    return 'matchups';
  }
  if (category.includes('-players_fixed_lines')) {
    if (participantId === participantHomeId) {
      return 'players-fixedLines-home';
    }
    if (participantId === participantAwayId) {
      return 'players-fixedLines-away';
    }
  }
  if (category.includes(PLAYER_HEADSHOTS_CATEGORY)) {
    if (participantId === participantHomeId) {
      return 'players-headshots-home';
    }
    if (participantId === participantAwayId) {
      return 'players-headshots-away';
    }
  }
  if (category.includes(PLAYER_ASSISTS_CATEGORY)) {
    if (participantId === participantHomeId) {
      return 'players-assists-home';
    }
    if (participantId === participantAwayId) {
      return 'players-assists-away';
    }
  }
  if (category.includes('-players')) {
    if (participantId === participantHomeId) {
      return 'players-home';
    }
    if (participantId === participantAwayId) {
      return 'players-away';
    }
  }
  return null;
};

const PlayerMarketGroupsOrder: PlayerMarketGroupCategory[] = [
  'players-home',
  'matchups',
  'players-away',
  'players-assists-home',
  'players-assists-away',
  'players-headshots-home',
  'players-headshots-away',
  'players-fixedLines-home',
  'players-fixedLines-away'
];

function getMarketGroups(
  match: PandapiMatchWithCompetitorsWithStatus,
  eventType: BlueprintEventType,
  eventId: number,
  markets: MarketBlock[],
  matchWinnerMarkets: MarketBlock[],
  shouldDisplayMicromarkets: boolean,
  rosters?: PandapiOpponents
) {
  const participantHomeId = rosters?.opponents.at(0)?.id ?? match.opponents.at(0)?.opponent.id;
  const participantAwayId = rosters?.opponents.at(1)?.id ?? match.opponents.at(1)?.opponent.id;

  const groupedMarkets = groupMarkets(match, eventType, eventId, markets, rosters)
    .sort((groupA, groupB) => {
      // Prebuilt last
      if (
        groupA.category === PREBUILT_CATEGORY ||
        groupA.category === PREBUILT_STORYBUILDER_TEMPLATE
      ) {
        return 1;
      }
      if (
        groupB.category === PREBUILT_CATEGORY ||
        groupB.category === PREBUILT_STORYBUILDER_TEMPLATE
      ) {
        return -1;
      }

      const groupAPlayerCategory = getPlayerMarketGroupCategory(
        groupA.category,
        groupA.participantId,
        participantHomeId,
        participantAwayId
      );
      const groupBPlayerCategory = getPlayerMarketGroupCategory(
        groupB.category,
        groupB.participantId,
        participantHomeId,
        participantAwayId
      );

      // If both groups are player market groups, sort according to PlayerMarketGroupsOrder
      if (groupAPlayerCategory && groupBPlayerCategory) {
        const groupAIndex = PlayerMarketGroupsOrder.indexOf(groupAPlayerCategory);
        const groupBIndex = PlayerMarketGroupsOrder.indexOf(groupBPlayerCategory);

        if (groupAIndex !== -1 && groupBIndex !== -1) {
          return groupAIndex - groupBIndex;
        }
      }

      if (groupAPlayerCategory && !groupBPlayerCategory) {
        return 1;
      }

      if (groupBPlayerCategory && !groupAPlayerCategory) {
        return -1;
      }

      return 0;
    })
    .filter(group => group.category !== 'micro' || shouldDisplayMicromarkets)
    .map(group => {
      if (group.category === 'micro') {
        const sortedMicromarkets = group.markets.sort(
          (marketA, marketB) =>
            -compareStrings(
              marketA.mostBalancedMarket.auto_deactivated_at ?? '',
              marketB.mostBalancedMarket.auto_deactivated_at ?? ''
            )
        );

        return {
          ...group,
          mainMarkets: sortedMicromarkets.slice(0, 2),
          markets: sortedMicromarkets.slice(2)
        };
      }
      return group;
    });

  return appendWinnerMarkets(groupedMarkets, matchWinnerMarkets, match.id, match.videogame.slug);
}

function isMatchUnbookable(match: PandapiMatch, userType: UserType): boolean {
  if (
    match.betting_metadata?.reviewed === false &&
    match.betting_metadata?.booked === true &&
    userType === 'pandascore'
  ) {
    return true;
  }

  if (
    match.betting_metadata?.booked === true &&
    match.betting_metadata?.markets_created === false &&
    userType === 'bookmaker'
  ) {
    return true;
  }

  return false;
}

const formatPlayersMatchup = (playersMatchups: [number, number][]) =>
  playersMatchups.map(matchUp => ({
    home_player_id: matchUp[0],
    away_player_id: matchUp[1]
  }));

const formatTeamRosters = (
  rosters: PandapiOpponents | undefined,
  subPlayers: Record<number, number[]>
) => {
  if (!rosters || !isRosterTeamType(rosters)) {
    return [];
  }
  const allSubPlayers = Object.values(subPlayers).flat();
  return rosters.opponents.map(opponent => ({
    team_id: opponent.id,
    player_ids: opponent.players
      .map(player => player.id)
      .filter(playerId => !allSubPlayers.includes(playerId))
  }));
};

const isPlayerMostKillMatchUpCreated = (marketGroups: MarketGroupData[]) => {
  const marketExist = marketGroups.find(
    market => market.category === PLAYER_MOST_KILLS_MATCHUP_CATEGORY
  );

  if (marketExist) {
    return marketExist;
  }

  return null;
};

const arePlayerFixedLinesCreated = (marketGroups: MarketGroupData[]) => {
  const marketExist = marketGroups.find(market => market.category.includes('players_fixed_lines'));

  return !!marketExist;
};

const createdPlayerMostKillMatchup = (marketGroups: MarketGroupData[]): PlayerMostKillMatchup[] => {
  const marketExist = isPlayerMostKillMatchUpCreated(marketGroups);

  if (marketExist) {
    return marketExist.mainMarkets.map(market => ({
      home_player_id: market.mostBalancedMarket?.player_home_id ?? null,
      away_player_id: market.mostBalancedMarket?.player_away_id ?? null
    }));
  }

  return [];
};

const canSendSettlements = (match: PandapiMatch): boolean =>
  ['live', 'finished', 'settled', 'canceled', 'postponed'].includes(match.status);

const getRouterMatchParams = (
  params: { matchId?: string; gameId?: string; view?: string } | undefined | null
): [string, string, MatchPageView | null] => {
  if (params) {
    const { matchId = '', gameId, view } = params;
    if (gameId) {
      if (view) {
        if (isMatchPageView(view)) {
          return [matchId, gameId, view];
        }
      }
      // handle the case where we are on a view but not on a game
      // in this case, the second param "gameId" is the view slug
      if (isMatchPageView(gameId)) {
        return [matchId, '', gameId];
      }
      return [matchId, gameId, null];
    }
    return [matchId, '', null];
  }

  return ['', '', null];
};

const getMatchGameViewUrl = (
  matchId: number,
  gameId: string | number | undefined,
  view: MatchPageView | undefined | null
): string => `/matches/${[matchId, gameId, view].filter(Boolean).join('/')}`;

const getUpdatesForBetbuilder = (
  currentData: MatchData,
  updatedMatch: MatchData['match']
): UpdatesForBetbuilder => ({
  markets: currentData.updatesForBetbuilder.markets,
  games: Date.now(),
  ...Object.fromEntries(
    currentData.match.games
      .filter(currentGame => {
        const updatedGame = updatedMatch.games.find(game => game.id === currentGame.id);
        if (!updatedGame) return false;
        return updatedGame.status !== currentGame.status;
      })
      .map(game => [game.id, Date.now()])
  )
});

const getStarPlayersMarkets = (marketGroups: MarketGroupData[]): number[] => [
  ...new Set(
    marketGroups
      .filter(group => group.category === PREBUILT_CATEGORY)
      .flatMap(group =>
        group.mainMarkets
          .filter(marketBlock =>
            isStarPlayerMarketTemplate(marketBlock.mostBalancedMarket.template)
          )
          .flatMap(marketBlock =>
            marketBlock.enabledMarkets.map(market => (market as PrebuiltMarket).player_id)
          )
      )
  )
];

const isPrebuiltStoryBuilder = (
  market: PrebuiltMarket | StoryBuilderMarket
): market is StoryBuilderMarket => market.template === PREBUILT_STORYBUILDER_TEMPLATE;

const areAllReviewedGameMarketsSettled = (gameMarkets: GameMarketBlock) =>
  gameMarkets.market_blocks
    .flatMap(block => [...block.enabledMarkets, ...block.disabledMarkets])
    .filter(market => market.reviewed)
    .every(market => market.status === 'pre_settled' || market.status === 'settled');

const arePreviousGamesSettled = (
  match: PandapiMatch,
  markets: MatchMarketBlocks,
  currentGame: PandapiGame
) => {
  const sortedGames = sortBy(match.games, 'position');
  const previousGames = sortedGames.slice(0, currentGame.position - 1);
  const previousGamesSettled = previousGames.every(({ id }) => {
    const gameMarkets = markets.games.find(({ id: gameId }) => gameId === id);
    if (gameMarkets) {
      return areAllReviewedGameMarketsSettled(gameMarkets);
    }
    return true;
  });

  return previousGamesSettled;
};

// Traders want to have team names first and then the match name
// https://pandascore.atlassian.net/browse/BET-2560
const formatMatchName = (matchName: string): string => {
  const regex = /^(.*?):\s*(.*?)\s+vs\s+(.*)$/;
  const regexResult = matchName.match(regex);
  if (regexResult) {
    const [, description, teamA, teamB] = regexResult;
    return `${teamA.trim()} vs ${teamB.trim()}, ${description.trim()}`;
  }
  // Not all match names are in this format, so we make sure to handle that
  return matchName;
};

// We transform the trader input game winner.model_helper into a competitor market because product
// want it to be displayed as a competitor market.
// https://pandascore.atlassian.net/browse/BET-2489
const updateHashiDataWithMatchData = (hashiData: HashiData, matchData: MatchData) => {
  if (
    matchData.markets?.trader_inputs?.inputs !== undefined &&
    matchData.match &&
    hashiData.competitors
  ) {
    const { inputs } = matchData.markets.trader_inputs;

    const helperModelInputs = inputs.filter(
      input =>
        input.event_type === 'game' && input.category === 'winner' && input.type === 'helper_model'
    );

    if (helperModelInputs) {
      const competitor = convertTraderInputsToCompetitor(helperModelInputs, matchData.match);

      const competitors = updateOrInsertInArray(
        hashiData.competitors,
        competitor,
        (competitorA, competitorB) => competitorA.slug === competitorB.slug
      );

      return {
        ...hashiData,
        competitors
      };
    }
  }

  return hashiData;
};

const convertTraderInputsToCompetitor = (inputs: TraderInputItem[], match: PandapiMatch) => {
  const competitorSlug = 'pandaodds-helper-model';

  const competitorMarkets = inputs.map(input =>
    convertTraderInputToCompetitorMarket(input, match, competitorSlug)
  );

  const competitor: CompetitorOddsWithStatus = {
    slug: competitorSlug,
    name: COMPETITORS_NAME[competitorSlug],
    markets: competitorMarkets,
    match_status: 'up',
    scrapper_status: 'up'
  };

  return competitor;
};

const convertTraderInputToCompetitorMarket = (
  input: TraderInputItem,
  match: PandapiMatch,
  competitorSlug: CompetitorSlug
) => {
  const [
    {
      type: opponentHomeType,
      opponent: { id: opponentHomeId }
    },
    {
      type: opponentAwayType,
      opponent: { id: opponentAwayId }
    }
  ] = match.opponents;

  const { supremacy } = input.value;

  const competitorMarket: CompetitorMarketState = {
    market_id: `${competitorSlug}-${input.videogame_slug}-game-${input.event_id}-winner-2-way`,
    competitor_slug: competitorSlug,
    event_type: match.number_of_games === 1 ? 'match' : 'game',
    event_id: match.number_of_games === 1 ? match.id : input.event_id,
    line: null,
    match_id: match.id,
    margin: null,
    participant_id: null,
    participant_type: null,
    status: 'active',
    template: 'winner-2-way',
    updated_at: input.updated_at,
    update_received_at: input.updated_at,
    videogame: input.videogame_slug,
    betting_stage: 'live',
    selections: [
      {
        position: 0,
        template: 'participant',
        probability: 0.5 + supremacy,
        probability_with_margin: 0.5 + supremacy,
        participant_type: opponentHomeType,
        participant_id: opponentHomeId,
        result: 'non_resulted',
        arbitrage_with_pandascore: false
      },
      {
        position: 1,
        template: 'participant',
        probability: 0.5 - supremacy,
        probability_with_margin: 0.5 - supremacy,
        participant_type: opponentAwayType,
        participant_id: opponentAwayId,
        result: 'non_resulted',
        arbitrage_with_pandascore: false
      }
    ]
  };

  return competitorMarket;
};

export {
  isMatchTradable,
  sortMarkets,
  getWinnerMarkets,
  groupMarkets,
  appendWinnerMarkets,
  getMarketGroupTraderInputs,
  getMarketSuggestions,
  isRosterTeamType,
  isPlayerMarketCreated,
  isAllPlayerMarketsCreated,
  orderMarketsDescending,
  getMarketGroups,
  isMatchUnbookable,
  getCreatedPlayersMarkets,
  formatPlayersMatchup,
  createdPlayerMostKillMatchup,
  canSendSettlements,
  getRouterMatchParams,
  getMatchGameViewUrl,
  arePlayerFixedLinesCreated,
  getUpdatesForBetbuilder,
  getStarPlayersMarkets,
  formatTeamRosters,
  isPrebuiltStoryBuilder,
  arePreviousGamesSettled,
  formatMatchName,
  updateHashiDataWithMatchData,
  getPlayerTraderInputTypeForCategory
};
